import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Drawer } from 'ksnm-common-ui/lib/Drawer';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';

import { publicationRegisterStore } from '@stores/publicationRegisterStore';

import { Form } from './Form';
import { validate } from './validationSchema';

import styles from './AddPackageDrawer.module.scss';

dayjs.extend(customParseFormat);

interface AddReportPackageDrawerInterface {
  visible: boolean;
  onClose: () => void;
}

export const AddPackageDrawer = observer(({ visible, onClose }: AddReportPackageDrawerInterface): JSX.Element => {
  const [title, setTitle] = useState('');

  const {
    isFormed,
    setIsFormed,
    packageFormInitial,
    savePackage,
    setPackageFormInitials,
    fetch,
    setEditPackageMode,
    clear,
  } = publicationRegisterStore;

  const initialValues = useMemo(() => ({ ...packageFormInitial }), [packageFormInitial]);

  const afterVisibleChange = useCallback(
    (open) => {
      if (!open) {
        setIsFormed(false);
        setEditPackageMode(false);
        setPackageFormInitials({});
      }
    },
    [setEditPackageMode, setIsFormed, setPackageFormInitials],
  );

  const onSubmit = useCallback(
    async (values) => {
      const validationResult = validate(values);
      if (validationResult) {
        return validationResult;
      }

      if (isFormed) {
        const result = await savePackage({
          ...values,
          year: dayjs(new Date(values.year)).format('YYYY'),
          creationData: dayjs(values.creationData, 'DD.MM.YYYY').format('YYYY-MM-DD'),
          disclosureData: dayjs(values.disclosureData, 'DD.MM.YYYY').format('YYYY-MM-DD'),
        });
        if (result) {
          clear(); // для сброса таблицы
          fetch();
          onClose();
        }
      }
    },
    [isFormed, savePackage, clear, fetch, onClose],
  );

  return (
    <Drawer open={visible} onClose={onClose} afterOpenChange={afterVisibleChange} title={title} resizable>
      <FormWrapper onSubmit={onSubmit} layout="vertical" className={styles.form} initialValues={initialValues}>
        <Form setTitle={setTitle} />
      </FormWrapper>
    </Drawer>
  );
});
